import { QueryKey } from "@tanstack/react-query";
import { ResponseError } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryConfig } from "@ws-react-query";
import {
  createV3PaginatedQueryHook,
  V3PaginatedQueryHook
} from "./createV3PaginatedQueryHook";
import {
  buildDefaultDependencies,
  V3DefaultDependencies
} from "./createV3MutationDependencies";
import {
  createV3MutationHook,
  resolveDependencies,
  V3MutationConfig,
  V3MutationHook
} from "./createV3MutationHook";
import { createV3QueryAllHook } from "./createV3QueryAllHook";
import {
  createV3QueryHook,
  SdkEndpointMethod,
  V3QueryHook
} from "./createV3QueryHook";
import {
  buildCacheHelpers,
  IsCleanEndpoint,
  isReadMethodName,
  listApiMethodNames,
  V3QueryKeyBuilder
} from "./createV3QueriesShared";

/** The request model of an SDK endpoint method. */
type V3RequestOf<TMethod> = TMethod extends (
  request: infer TRequest,
  ...rest: any[]
) => Promise<any>
  ? TRequest
  : never;

/** The response model of an SDK endpoint method. */
type V3ResponseOf<TMethod> = TMethod extends (
  request: any,
  ...rest: any[]
) => Promise<infer TResponse>
  ? TResponse
  : never;

/**
 * Whether an endpoint's response is cursor-paginated — i.e. it actually declares
 * a `pagination` field carrying the `{ nextPageToken }` cursor the paginated hooks
 * advance. The `keyof` test is the real gate: an optional `pagination?` property
 * alone is structurally permissive (almost every object "has" one), so we require
 * the key to be declared, then confirm its value carries the cursor.
 *
 * This is the guard that decides whether a read entry also gets `usePaginatedQuery`
 * + `useQueryAll` (see {@link V3ReadEntry}). It keys off the *response type*, so a
 * `search*` that one day returns a paginated `*List` picks the paginated hooks up
 * automatically — no name list to maintain. The leading `never` guard keeps an
 * error-only `Promise<never>` endpoint from resolving to the paginated branch.
 */
type IsCursorPaginated<TResponse> = [TResponse] extends [never]
  ? false
  : TResponse extends { pagination?: infer TPagination }
    ? "pagination" extends keyof TResponse
      ? NonNullable<TPagination> extends { nextPageToken?: string }
        ? true
        : false
      : false
    : false;

/**
 * Seeds the query cache for a request with prefetched data (e.g. from app
 * bootstrap), under the same key {@link V3QueryHook} reads. The request is
 * positional (so it targets a specific cache entry) and required — pass
 * `undefined` for the no-request variant of an optional-request endpoint.
 */
type V3QuerySetter<TRequest, TResponse> = (
  request: TRequest,
  data: TResponse
) => void;

/**
 * Reads the currently cached response for a request out of the query cache, off
 * the same key {@link V3QueryHook} reads — `undefined` when nothing is cached. The
 * synchronous read counterpart to {@link V3QuerySetter}; the request mirrors the
 * SDK's optionality (omit it for the no-request variant), like
 * {@link V3QueryKeyBuilder}.
 */
type V3QueryGetter<TRequest, TResponse> = undefined extends TRequest
  ? (request?: TRequest) => TResponse | undefined
  : (request: TRequest) => TResponse | undefined;

/**
 * Reads the currently cached error for a request out of the query cache, off the
 * same key {@link V3QueryHook} reads — `undefined` when the query has no error (it
 * is unfetched, loading, or succeeded). The error counterpart to
 * {@link V3QueryGetter}; the request mirrors the SDK's optionality.
 */
type V3QueryErrorGetter<TRequest> = undefined extends TRequest
  ? (request?: TRequest) => ResponseError | undefined
  : (request: TRequest) => ResponseError | undefined;

/**
 * Seeds the cached error state for a request, under the same key
 * {@link V3QueryHook} reads, so an observing `useQuery` reflects the failure. The
 * error counterpart to {@link V3QuerySetter}; the request is positional and
 * required — pass `undefined` for the no-request variant.
 */
type V3QueryErrorSetter<TRequest> = (
  request: TRequest,
  error: ResponseError
) => void;

/**
 * Imperatively resolves the read — outside React — seeding the cache with the
 * outcome (response via {@link V3QuerySetter}, or error via
 * {@link V3QueryErrorSetter}) under the same key {@link V3QueryHook} reads, then
 * resolves with the response or rejects with the SDK {@link ResponseError}. The
 * shared signature of the cache-first `fetchQueryData` and the always-fetch
 * `forceFetchQueryData`; the request mirrors the SDK's optionality, like
 * {@link V3QueryKeyBuilder}.
 */
type V3QueryFetcher<TRequest, TResponse> = undefined extends TRequest
  ? (request?: TRequest) => Promise<TResponse>
  : (request: TRequest) => Promise<TResponse>;

/**
 * The cache helpers every read entry carries, on the single-value `useQuery`
 * cache key `[apiName, methodName, request]`. `queryKeyBase` is the request-less
 * `[apiName, methodName]` — broad enough to invalidate every cached request of the
 * method across *both* the single and the `"paginated"` cache spaces at once.
 * `getQueryData` / `setQueryData` read and seed the cached response, and
 * `getQueryError` / `setQueryError` its cached error, off that single key
 * synchronously. `fetchQueryData` / `forceFetchQueryData` run the read
 * imperatively, seeding the same key with the response or the error.
 */
type V3ReadCacheHelpers<TRequest, TResponse> = {
  getQueryKey: V3QueryKeyBuilder<TRequest>;
  queryKeyBase: QueryKey;
  getQueryData: V3QueryGetter<TRequest, TResponse>;
  setQueryData: V3QuerySetter<TRequest, TResponse>;
  getQueryError: V3QueryErrorGetter<TRequest>;
  setQueryError: V3QueryErrorSetter<TRequest>;
  /**
   * Cache-first: resolves the cached response when one is present, otherwise
   * fetches once (delegating to `forceFetchQueryData`).
   */
  fetchQueryData: V3QueryFetcher<TRequest, TResponse>;
  /**
   * Always hits the endpoint, refreshing the cache with the response (or the
   * error) regardless of what is already cached.
   */
  forceFetchQueryData: V3QueryFetcher<TRequest, TResponse>;
};

/**
 * The paginated read hooks, added to a read entry only when its response is
 * cursor-paginated ({@link IsCursorPaginated}). Both walk the v3 `{ size, token }`
 * cursor over one shared `[apiName, methodName, "paginated", request]` cache entry:
 * - `usePaginatedQuery` — on-demand pages for infinite scroll (`data.pages`,
 *   `fetchNextPage`, `hasNextPage`, …).
 * - `useQueryAll` — the same query driven to completion automatically, so
 *   `data.pages` holds the whole list. Use it when a screen needs the full
 *   collection rather than scroll.
 * Both expose `flatData`: every page's items flattened into one typed array.
 */
type V3PaginatedReadHooks<TRequest, TResponse> = {
  usePaginatedQuery: V3PaginatedQueryHook<TRequest, TResponse>;
  useQueryAll: V3PaginatedQueryHook<TRequest, TResponse>;
};

/** The always-present part of a read entry: the single `useQuery` + cache helpers. */
type V3QueryEntryBase<TRequest, TResponse> = {
  useQuery: V3QueryHook<TRequest, TResponse>;
} & V3ReadCacheHelpers<TRequest, TResponse>;

/**
 * A read leaf (`get*` / `list*` / `search*`): always a single-value `useQuery`
 * plus its cache helpers, and — gated on the response type — the
 * {@link V3PaginatedReadHooks} pair. Cursor-paginated responses (the `*List`
 * collections, or any future paginated `search*`) get `usePaginatedQuery` +
 * `useQueryAll`; non-paginated reads (`getAccount`, `getAccountSettings`, …) get
 * just `useQuery`.
 */
type V3ReadEntry<TMethod> = TMethod extends (
  request: infer TRequest,
  ...rest: any[]
) => Promise<infer TResponse>
  ? V3QueryEntryBase<TRequest, TResponse> &
      (IsCursorPaginated<TResponse> extends true
        ? V3PaginatedReadHooks<TRequest, TResponse>
        : {})
  : never;

/**
 * A single mutation leaf: the generated mutation hook plus the
 * `defaultDependencies` it auto-invalidates — the per-level (`api` / `entity`)
 * sibling-read keys derived from the method name. The hook injects these on
 * success per the call's `defaultDependenciesLevel`; the field is also exposed
 * for inspection / explicit reuse. Mutations have no cache key of their own, so —
 * unlike a read entry — there are no `getQueryKey` / `setQueryData` helpers here.
 */
type V3MutationEntry<TMethod> = TMethod extends (
  request: infer TRequest,
  ...rest: any[]
) => Promise<infer TResponse>
  ? {
      useMutation: V3MutationHook<TRequest, TResponse>;
      defaultDependencies: V3DefaultDependencies;
    }
  : never;

/**
 * The registry entry for one endpoint, chosen by the method-name verb — the only
 * signal available at runtime (TypeScript types are erased) — and refined to the
 * SDK's request/response types at compile time:
 * - `get*` / `list*` / `search*` → a read ({@link V3ReadEntry}): always `useQuery`
 *   (+ cache helpers), plus `usePaginatedQuery` + `useQueryAll` when the response
 *   is cursor-paginated.
 * - everything else (`create*`, `update*`, `delete*`, `set*`, `move*`, …) → a
 *   mutation: `{ useMutation, defaultDependencies }`.
 *
 * The two buckets partition the clean endpoints exactly — every method lands in
 * one — so the result type carries precisely the hook(s) (and cache helpers) that
 * endpoint supports, and nothing it doesn't.
 */
type V3ApiEntry<TApi, K extends keyof TApi> = K extends
  | `get${string}`
  | `list${string}`
  | `search${string}`
  ? V3ReadEntry<TApi[K]>
  : V3MutationEntry<TApi[K]>;

/** The full registry for one API instance, keyed by clean endpoint method name. */
export type V3Api<TApi> = {
  [K in keyof TApi as IsCleanEndpoint<TApi, K> extends true
    ? K
    : never]: V3ApiEntry<TApi, K>;
};

/**
 * The construction-time default config for one endpoint, typed to that method's
 * kind: a read takes a {@link WSQueryConfig} (applied to its `useQuery`); a
 * mutation takes a {@link V3MutationConfig}.
 *
 * The dominant use is mutation invalidation that the name-derived defaults can't
 * know — e.g. a cross-resource `dependencies` on `updateAccount` — declared once
 * here instead of at every call site. (A read's paginated hooks take their config
 * per call, not from here.)
 */
type V3MethodConfig<TApi, K extends keyof TApi> = K extends
  | `get${string}`
  | `list${string}`
  | `search${string}`
  ? WSQueryConfig<V3ResponseOf<TApi[K]>, ResponseError>
  : V3MutationConfig<V3RequestOf<TApi[K]>, V3ResponseOf<TApi[K]>>;

/**
 * A factory that derives one method's config from the registry being built. This
 * is always the factory form: it receives `queries` — this same registry — so a
 * config can reference its own `getQueryKey` / `queryKeyBase` (a cross-read
 * dependency the name-derived defaults can't infer) without a `const`
 * self-reference cycle. A config that doesn't need the registry just ignores the
 * argument: `() => ({ … })`.
 *
 * Reference registry entries only from inside the lazy `dependencies` /
 * `awaitDependencies` callbacks, not at the top of the factory body: the factory
 * runs during construction, while the registry is still being populated, but those
 * callbacks fire at mutation time, by which point it is complete.
 */
type V3MethodConfigFactory<TApi, K extends keyof TApi> = (
  queries: V3Api<TApi>
) => V3MethodConfig<TApi, K>;

/**
 * Optional per-method default config passed to {@link createV3Queries}, keyed by
 * clean endpoint method name. Every entry is a factory `(queries) => config`
 * ({@link V3MethodConfigFactory}) — receiving the registry being built, so a config
 * can reference this registry's own keys — and optional: omit a method to leave it
 * on the generated defaults.
 */
export type V3ApiConfig<TApi> = {
  [K in keyof TApi as IsCleanEndpoint<TApi, K> extends true
    ? K
    : never]?: V3MethodConfigFactory<TApi, K>;
};

type MutationDependencies = V3MutationConfig<unknown, unknown>["dependencies"];

/**
 * Concatenate two dependency declarations into one — the union of both, resolved
 * at mutation time. Used to layer a registry-config `dependencies` /
 * `awaitDependencies` under the per-call one so the configured keys are always
 * applied, never replaced by a call that happens to pass its own. Returns the
 * single non-empty side untouched (and `undefined` when both are absent).
 */
const combineDependencies = (
  base: MutationDependencies,
  call: MutationDependencies
): MutationDependencies => {
  if (!base) return call;
  if (!call) return base;
  return (result, variables) => [
    ...resolveDependencies(base, result, variables),
    ...resolveDependencies(call, result, variables)
  ];
};

/**
 * Merge a read method's registry-config defaults with the per-call config — a
 * shallow merge where the call wins, so the config is a true default. Returns the
 * single defined side untouched when only one is present.
 */
const mergeReadConfig = <TConfig extends object>(
  base: TConfig | undefined,
  call: TConfig | undefined
): TConfig | undefined => {
  if (!base) return call;
  if (!call) return base;
  return { ...base, ...call };
};

/**
 * Merge a mutation method's registry-config defaults with the per-call config:
 * the call wins for scalars / callbacks (so the config is a default), but
 * `dependencies` and `awaitDependencies` are *combined* — the configured
 * invalidation keys always fire alongside the call's, never instead of them.
 */
const mergeMutationConfig = (
  base: V3MutationConfig<unknown, unknown> | undefined,
  call: V3MutationConfig<unknown, unknown> | undefined
): V3MutationConfig<unknown, unknown> | undefined => {
  if (!base) return call;
  if (!call) return base;
  return {
    ...base,
    ...call,
    dependencies: combineDependencies(base.dependencies, call.dependencies),
    awaitDependencies: combineDependencies(
      base.awaitDependencies,
      call.awaitDependencies
    )
  };
};

/**
 * Builds the registry leaf for one endpoint, branching on the method name the
 * same way {@link V3ApiEntry} branches on its type — so runtime shape and static
 * type stay in lockstep.
 *
 * Reads always get a single `useQuery` plus the paginated pair. Types are erased
 * at runtime, so the paginated hooks are built for *every* read; the
 * {@link IsCursorPaginated} type guard then exposes them only on reads whose
 * response carries a cursor (today the `*List` collections; tomorrow a paginated
 * `search*`). The hooks are harmless on a non-paginated response — they're just
 * never reachable through the typed registry.
 */
const buildApiEntry = (params: {
  api: object;
  apiName: string;
  methodName: string;
  readMethodNames: string[];
  methodConfig: unknown;
}): Record<string, unknown> => {
  const { api, apiName, methodName, readMethodNames, methodConfig } = params;

  if (isReadMethodName(methodName)) {
    // Single read on `[apiName, methodName, request]`; the paginated pair shares
    // its own `[apiName, methodName, "paginated", request]` entry. The single
    // `queryKeyBase` (`[apiName, methodName]`) covers both for invalidation.
    const singleKey = [apiName, methodName];
    const paginatedKey = [apiName, methodName, "paginated"];
    const queryHook = createV3QueryHook<any, any>(api, methodName, singleKey);
    const callEndpoint = (api as Record<string, SdkEndpointMethod>)[
      methodName
    ].bind(api);
    const defaultConfig = methodConfig as
      | WSQueryConfig<unknown, ResponseError>
      | undefined;
    const useQuery = (
      request?: unknown,
      callConfig?: WSQueryConfig<unknown, ResponseError>
    ) => queryHook(request, mergeReadConfig(defaultConfig, callConfig));
    return {
      useQuery,
      usePaginatedQuery: createV3PaginatedQueryHook<any, any>(
        api,
        methodName,
        paginatedKey
      ),
      useQueryAll: createV3QueryAllHook<any, any>(
        api,
        methodName,
        paginatedKey
      ),
      ...buildCacheHelpers(singleKey, callEndpoint)
    };
  }

  // Everything else → mutation, with name-derived default invalidation keys.
  const defaultDependencies = buildDefaultDependencies({
    apiName,
    mutationMethodName: methodName,
    readMethodNames
  });
  const baseHook = createV3MutationHook<any, any>(
    api,
    methodName,
    defaultDependencies
  );
  const defaultConfig = methodConfig as
    | V3MutationConfig<unknown, unknown>
    | undefined;
  const useMutation = (callConfig?: V3MutationConfig<unknown, unknown>) =>
    baseHook(mergeMutationConfig(defaultConfig, callConfig));
  return { useMutation, defaultDependencies };
};

/**
 * Build a single fully-typed registry of React Query hooks from one SDK API
 * instance — the merge of the former per-kind query / paginated-query / mutation
 * factories into one.
 *
 * Every clean endpoint becomes `<methodName>.<hook>`, mirroring the SDK call 1:1
 * (same request type & optionality, same response type, full config passthrough).
 * The verb in the method name decides which hook(s) the entry carries:
 * - `get*` / `list*` / `search*` → a read: always `useQuery` (single-value
 *   `WSQueryResult`), plus — when the response is cursor-paginated —
 *   `usePaginatedQuery` (on-demand pages for scroll) and `useQueryAll` (the same
 *   query driven to completion, so `data.pages` holds the whole list). The
 *   paginated pair is gated on the *response type* ({@link IsCursorPaginated}), so
 *   the `*List` collections get it today and a paginated `search*` would get it
 *   the moment its response gains a cursor — no name list to maintain.
 * - everything else (`create*`, `update*`, `delete*`, `set*`, `move*`, …) →
 *   `useMutation` (+ `defaultDependencies`).
 *
 * Reads also carry `getQueryKey` / `queryKeyBase` / `getQueryData` /
 * `setQueryData` / `getQueryError` / `setQueryError` / `fetchQueryData` /
 * `forceFetchQueryData`. Cache keys
 * are
 * derived from `apiName` + method — `[apiName, methodName, request]` for the
 * single read, `[apiName, methodName, "paginated", request]` for the paginated
 * hooks (shared by `usePaginatedQuery` and `useQueryAll`); `queryKeyBase`
 * (`[apiName, methodName]`) covers both. No hand-written `QUERY_V3_*` constants or
 * `useWSQuery` calls. The `*Raw` / `*RequestOpts` SDK companions are excluded.
 *
 * **Mutation default invalidation.** Each mutation auto-invalidates the sibling
 * reads its name implies — `updateAccount` refreshes `getAccount`, `listAccounts`
 * and `searchAccounts` (the `[apiName, methodName]` prefix matches both the single
 * and paginated cache spaces). Tune the breadth per call with
 * `defaultDependenciesLevel` (`"entity"` default, `"api"`, `"none"`); explicit
 * `dependencies` merge with the defaults rather than replacing them.
 *
 * **Per-method config.** The optional `config` declares construction-time defaults
 * per method, typed to that method's kind (see {@link V3ApiConfig}). Its main use
 * is invalidation the name-derived defaults can't infer — a cross-resource
 * `dependencies` on a mutation, declared once instead of at every call site. A
 * mutation's configured `dependencies` / `awaitDependencies` are *combined* with
 * (never replaced by) the per-call ones; a read's config is a plain `useQuery`
 * default the call overrides.
 *
 * Every entry is a factory `(queries) => config`. `queries` is the registry being
 * built, so a config's lazy `dependencies` can reference *this* registry's own keys
 * — a cross-read dep the name-derived defaults can't infer, e.g. a transfer that
 * must invalidate a sibling balance read — with no `const` self-reference cycle and
 * no explicit `: V3Api<…>` annotation. A config that doesn't need the registry just
 * ignores the argument: `() => ({ … })`. (To reference *another* registry's keys,
 * define that registry first and close over it.)
 *
 * @param apiName The cache-key namespace (an explicit string — robust across
 *                minified builds, where class names are mangled). Keys it as
 *                `[apiName, …]`, and keeps same-named methods on sibling apis apart.
 * @param api     An instantiated SDK API client, e.g. `accountsApi`.
 * @param config  Optional per-method config factories (see {@link V3ApiConfig}).
 *
 * @example
 * // src/v3/api/queries/queries.ts
 * import { accountsApi, internalAccountsApi, personsApi } from "@api";
 *
 * const persons = createV3Queries("personsApi", personsApi);
 * const accounts = createV3Queries("accountsApi", accountsApi, {
 *   // updateAccount should also refresh the owning person — a cross-resource dep
 *   // the name-derived defaults can't infer (combined with the call's own). This
 *   // factory ignores `queries`; it closes over the separate `persons` registry.
 *   updateAccount: () => ({
 *     dependencies: (_res, req) =>
 *       [persons.getPerson.getQueryKey({ personId: req.accountUpdate.ownerId })]
 *   })
 * });
 *
 * // a same-registry dep: the factory's `queries` arg is this very registry, so a
 * // transfer can invalidate its sibling balance read with no self-reference cycle
 * const internalAccounts = createV3Queries("internalAccountsApi", internalAccountsApi, {
 *   createInternalAccountTransfer: queries => ({
 *     dependencies: () => [queries.getInternalAccountBalance.queryKeyBase]
 *   })
 * });
 * export const api = { accountsApi: accounts, personsApi: persons };
 *
 * const queryAccount = api.accountsApi.getAccount.useQuery({ accountId }); // single
 * const paginated = api.accountsApi.listAccounts.usePaginatedQuery();      // scroll
 * const everyAccount = api.accountsApi.listAccounts.useQueryAll();         // whole list
 * const allAccounts = everyAccount.flatData; // SDK items, flattened across pages
 * const [updateAccount] = api.accountsApi.updateAccount.useMutation();
 */
export function createV3Queries<TApi extends object>(
  apiName: string,
  api: TApi,
  config: V3ApiConfig<TApi> = {}
): V3Api<TApi> {
  const registry: Record<string, unknown> = {};
  const configByMethod = config as Record<
    string,
    ((queries: V3Api<TApi>) => unknown) | undefined
  >;
  // Sibling reads on the same client power the entity-level mutation defaults.
  const readMethodNames = listApiMethodNames(api, isReadMethodName);
  // Passed to each method's config factory so it can close over its own registry;
  // its lazy `dependencies` callback reads the entries this loop populates below.
  const self = registry as unknown as V3Api<TApi>;

  for (const methodName of listApiMethodNames(api, () => true)) {
    registry[methodName] = buildApiEntry({
      api,
      apiName,
      methodName,
      readMethodNames,
      methodConfig: configByMethod[methodName]?.(self)
    });
  }

  return registry as unknown as V3Api<TApi>;
}
